import useTranslation from "@babackapp/translations/use-translation";
import { type ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import { useMemo } from "react";
import { SizingRecommendationBadge } from "./sizing-recommendation-badge";

type SizingDiagnosticRow = {
  productId: string;
  productTitle: string;
  imageUrl: string;
  recommendation: "runs_large" | "runs_small" | null;
  ratio: number | null;
  sampleSize: number;
  sizeTooLargeCount: number;
  sizeTooSmallCount: number;
};

export function useSizingTableColumns(): ColumnDef<SizingDiagnosticRow>[] {
  const t = useTranslation();

  return useMemo(
    () => [
      {
        accessorKey: "productTitle",
        header: t("sizing.table.product"),
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {row.original.imageUrl && (
              <img
                src={row.original.imageUrl}
                alt={row.original.productTitle}
                className="h-10 w-10 rounded-md object-cover"
              />
            )}
            <span className="font-medium truncate max-w-[200px]">
              {row.original.productTitle}
            </span>
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "recommendation",
        header: t("sizing.table.recommendation"),
        cell: ({ row }) => (
          <SizingRecommendationBadge
            recommendation={row.original.recommendation}
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: "ratio",
        header: ({ column }) => (
          <button
            type="button"
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("sizing.table.ratio")}
            <ArrowUpDown className="h-3.5 w-3.5" />
          </button>
        ),
        cell: ({ row }) =>
          row.original.ratio != null
            ? `${(row.original.ratio * 100).toFixed(1)}%`
            : "—",
      },
      {
        id: "sizingReturns",
        header: t("sizing.table.sizingReturns"),
        cell: ({ row }) =>
          row.original.sizeTooLargeCount + row.original.sizeTooSmallCount,
        enableSorting: false,
      },
      {
        accessorKey: "sampleSize",
        header: ({ column }) => (
          <button
            type="button"
            className="flex items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("sizing.table.sampleSize")}
            <ArrowUpDown className="h-3.5 w-3.5" />
          </button>
        ),
        cell: ({ row }) => row.original.sampleSize,
      },
    ],
    [t],
  );
}
